import React from "react";
import styles from "./MetadataDeploymentHelp.module.scss";

const MetadataDeploymentHelp: React.FC = () => {
  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <header className={styles.hero}>
          <p className={styles.kicker}>WADE Insight &ndash; Metadata Deployment</p>
          <h1>Overview</h1>
          <p>
            The Metadata Deployment function in WADE Insight enables controlled and governed
            promotion of metadata between environments, typically from Development to Test and
            Production. This ensures consistency, traceability, and stability across environments
            while preserving a single source of truth for metadata definitions.
          </p>
          <p>
            Metadata deployment does not move data. It deploys metadata definitions that control how
            data is ingested, transformed, validated, and loaded in the data platform.
          </p>
        </header>

        <section className={styles.section}>
          <h2>Key Concepts</h2>
          <div className={styles.cardGroup}>
            <article>
              <h3>Master Environment</h3>
              <ul>
                <li>Only one environment can act as the Master Environment.</li>
                <li>The Master Environment is the source of truth for metadata.</li>
                <li>Typically, this is the Development environment.</li>
                <li>Metadata is always deployed from the Master Environment to one or more target environments.</li>
              </ul>
            </article>
            <article>
              <h3>Rules in Target Environments</h3>
              <p>In target environments (e.g. TEST, PROD):</p>
              <ul>
                <li>
                  <strong>❌ You cannot create new entities</strong>, such as Data Sources, Data Source Endpoints,
                  ETL Jobs, Job Task Groups, Job Tasks, Data Vault entities, or Data Mart entities.
                </li>
                <li>
                  <strong>✅ You can update environment-specific attributes</strong> like server or host names,
                  connection strings, credentials / key references, and runtime parameters.
                </li>
              </ul>
              <p>This guarantees consistent structure and logic while allowing environment-specific configuration.</p>
            </article>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Metadata Comparison Between Environments</h2>
          <p>When comparing metadata between two environments, WADE Insight presents differences across three hierarchical levels.</p>
          <article>
            <h3>Level 1 &ndash; ETL Job (Highest Level)</h3>
            <ul>
              <li>Represents an end-to-end orchestration unit.</li>
              <li>Shows ETL Jobs and dependencies between jobs.</li>
              <li>Indicates whether a job is New, Updated, or Unchanged.</li>
            </ul>
          </article>
          <article>
            <h3>Level 2 &ndash; Job Task Group</h3>
            <ul>
              <li>Each ETL Job contains one or more Job Task Groups.</li>
              <li>Provides logical grouping of related processing steps for parallelization.</li>
              <li>Changes reflect modifications to execution flow.</li>
            </ul>
          </article>
          <article>
            <h3>Level 3 &ndash; Job Task (Lowest Level)</h3>
            <p>Examples include:</p>
            <ul>
              <li>Ingesting data from a source endpoint.</li>
              <li>Transforming data between layers.</li>
              <li>Loading Data Vault or Data Mart entities.</li>
              <li>Executing data quality procedures.</li>
              <li>Running custom notebooks or stored procedures.</li>
            </ul>
          </article>
        </section>

        <section className={styles.section}>
  <h2>Metadata Storage Model</h2>
  <p>
    All WADE metadata resides in the Metadata Catalog, implemented as a structured database data model.
  </p>

  <p>Metadata Hierarchy:</p>
  <ul className={styles.hierarchy}>
    <li>
      ETL Job
      <ul>
        <li>
          Job Task Group
          <ul>
            <li>Job Task</li>
          </ul>
        </li>
      </ul>
    </li>
  </ul>

  <p>This hierarchy governs orchestration, dependencies, and deployment granularity.</p>

  {/* ✅ Add this block at the end */}
  <h3>Status &amp; Warnings (Source-to-Target Comparison)</h3>
  <p>
    WADE assigns a <strong>status</strong> to each entity based on a comparison between the source metadata and the
    deployed/target metadata. These statuses indicate what changed and help determine what should be included in
    deployments.
  </p>

  <h4>Status Types</h4>
  <ul className={styles.hierarchy}>
    <li>
      <strong>NO_CHANGE</strong> — Source and target are identical; no action required.
    </li>
    <li>
      <strong>NEW</strong> — Entity exists in the source but not in the target (newly introduced).
    </li>
    <li>
      <strong>UPDATED</strong> — Entity exists in both source and target, but one or more attributes differ.
    </li>
    <li>
      <strong>DELETED</strong> — Entity no longer exists in the source but still exists in the target (removed from source).
    </li>
    <li>
      <strong>CHILD_MODIFIED</strong> — Used for parent nodes in the hierarchy when the parent itself is unchanged, but one or
      more children under it have a status other than <strong>NO_CHANGE</strong>. For example, if a <em>Job Task</em> is UPDATED,
      its containing <em>Job Task Group</em> and <em>ETL Job</em> may be marked as CHILD_MODIFIED.
    </li>
    <li>
      <strong>WARNING</strong> — Indicates detectable changes that require attention, even when the primary entity may show
      NO_CHANGE (e.g., deeper-level column changes or dependency relationship changes).
    </li>
  </ul>

  <h4>Parent Propagation (Nested Structure)</h4>
  <p>
    Because metadata is hierarchical, WADE propagates change awareness upward. If any child element (Job Task Group or Job
    Task) is NEW/UPDATED/DELETED, the immediate parent and all ancestors are flagged as <strong>CHILD_MODIFIED</strong> to
    reflect that something inside the subtree changed.
  </p>

  <h4>Warnings</h4>
  <p>
    In addition to the primary statuses, WADE can raise warnings that highlight important change contexts:
  </p>
  <ul className={styles.hierarchy}>
    <li>
      <strong>Source Entity Column Changes Detected</strong> — Changes were detected at the <em>source_entity_column</em> level
      (e.g., columns added/updated/deleted). These are tracked separately because column-level modifications may affect
      downstream modeling and deployment even if the higher-level entity appears unchanged.
    </li>
    <li>
      <strong>Dependency Changes Detected</strong> — The job/task definitions may be unchanged, but the <em>dependency graph</em>
      has changed (e.g., dependencies between jobs or between job task groups were added/removed/updated). In this case the
      entity’s core metadata can remain NO_CHANGE, while WADE flags the relationship updates as a warning so orchestration
      and execution order remain correct.
    </li>
  </ul>
</section>

        <section className={styles.section}>
          <h2>Job Task to Metadata Entity Mapping</h2>
          <p>Each Job Task is connected to exactly one operational responsibility.</p>
          <h3>Supported Job Task Types</h3>
          <ul>
            <li>Ingest Source Entity &ndash; e.g., ingest Customer endpoint (CSV) from Dynamics 365.</li>
            <li>Transform Source Entity &ndash; e.g., transform Customer CSV to a Bronze Delta/Parquet table.</li>
            <li>Load Data Vault Entity (Silver) &ndash; e.g., execute load procedure for a Customer Satellite.</li>
            <li>Load Data Mart Entity (Gold) &ndash; e.g., execute load procedure for a Customer Dimension.</li>
            <li>Execute Custom Logic &ndash; e.g., custom notebook, stored procedure, or platform-specific script.</li>
          </ul>
          <p>All metadata entities are connected through Job Tasks, except virtualized SQL views for Data Vault, which are not tied to Job Tasks.</p>
        </section>

        <section className={styles.section}>
          <h2>Deployment Package</h2>
          <h3>Selecting Metadata for Deployment</h3>
          <ul>
            <li>You can select metadata at the ETL Job, Job Task Group, or Job Task level.</li>
            <li>Once selected, metadata is automatically added to a Deployment Package.</li>
          </ul>
          <h3>Deployment Package Behavior</h3>
          <ul>
            <li>Includes all required dependencies automatically.</li>
            <li>Ensures consistency across metadata relationships.</li>
            <li>Acts as a versioned deployment unit.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Deploying to a Target Environment</h2>
          <h3>Steps</h3>
          <ol>
            <li>Select metadata components (Job, Task Group, or Task).</li>
            <li>Review the generated Deployment Package.</li>
            <li>Drag the Deployment Package to a Target Environment.</li>
            <li>Validate differences and environment-specific attributes.</li>
            <li>Execute the deployment.</li>
          </ol>
          <h3>Example</h3>
          <p>
            Deploy metadata from source <strong>NexaSales DEV</strong> (Master Environment) to target <strong>NexaSales PROD</strong>.
            The deployment promotes updated ETL Jobs and related components, preserves PROD-specific configuration values,
            and prevents structural drift between environments.
          </p>
        </section>

        <section className={styles.section}>
          <h2>Benefits of Metadata Deployment</h2>
          <ul>
            <li>Single source of truth for metadata.</li>
            <li>Controlled and auditable promotions.</li>
            <li>Reduced risk in production deployments.</li>
            <li>Clear separation of structure vs configuration.</li>
            <li>Enterprise-grade governance.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Summary</h2>
          <p>
            WADE Insight&rsquo;s Metadata Deployment provides a robust, governed approach to promoting data orchestration metadata across environments.
            By enforcing a Master Environment and structured deployment hierarchy, organizations ensure reliability, consistency,
            and scalability in their data platforms.
          </p>
        </section>
      </div>
    </div>
  );
};

export default MetadataDeploymentHelp;
